import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,  TextField,
  Checkbox,
  Alert,
  CircularProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Role, Permission, CreateRoleRequest, UpdateRoleRequest } from '../types/auth';
import { authService } from '../services/authService';
import PermissionGate from '../components/Common/PermissionGate';
import { usePermission } from '../hooks/usePermissions';

const RoleManagementPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [createForm, setCreateForm] = useState<CreateRoleRequest>({
    name: '',
    description: '',
    permissionIds: [],
  });

  const [editForm, setEditForm] = useState<UpdateRoleRequest>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [rolesData, permissionsData] = await Promise.all([
        authService.getRoles(),
        authService.getPermissions(),
      ]);
      setRoles(rolesData);
      setPermissions(permissionsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    try {
      setError(null);
      await authService.createRole(createForm);
      setIsCreateDialogOpen(false);
      setCreateForm({
        name: '',
        description: '',
        permissionIds: [],
      });
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create role');
    }
  };

  const handleEditRole = async () => {
    if (!selectedRole) return;

    try {
      setError(null);
      await authService.updateRole(selectedRole.id, editForm);
      setIsEditDialogOpen(false);
      setSelectedRole(null);
      setEditForm({});
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update role');
    }
  };

  const handleDeleteRole = async () => {
    if (!selectedRole) return;

    try {
      setError(null);
      await authService.deleteRole(selectedRole.id);
      setIsDeleteDialogOpen(false);
      setSelectedRole(null);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete role');
    }
  };

  const openEditDialog = (role: Role) => {
    setSelectedRole(role);
    setEditForm({
      name: role.name,
      description: role.description,
      permissionIds: role.permissions.map(p => p.id),
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (role: Role) => {
    setSelectedRole(role);
    setIsDeleteDialogOpen(true);
  };

  const getRoleColor = (roleName: string) => {
    switch (roleName) {
      case 'SuperAdmin': return 'error';
      case 'Admin': return 'warning';
      case 'Manager': return 'primary';
      case 'Operator': return 'secondary';
      case 'Viewer': return 'default';
      default: return 'default';
    }
  };

  const groupPermissionsByResource = (permissions: Permission[]) => {
    const grouped: Record<string, Permission[]> = {};
    permissions.forEach(permission => {
      if (!grouped[permission.resource]) {
        grouped[permission.resource] = [];
      }
      grouped[permission.resource].push(permission);
    });
    return grouped;
  };

  const PermissionSelector: React.FC<{
    selectedPermissionIds: string[];
    onChange: (permissionIds: string[]) => void;
  }> = ({ selectedPermissionIds, onChange }) => {
    const groupedPermissions = groupPermissionsByResource(permissions);

    const handlePermissionToggle = (permissionId: string) => {
      const newSelection = selectedPermissionIds.includes(permissionId)
        ? selectedPermissionIds.filter(id => id !== permissionId)
        : [...selectedPermissionIds, permissionId];
      onChange(newSelection);
    };

    const handleResourceToggle = (resource: string) => {
      const resourcePermissions = groupedPermissions[resource];
      const resourcePermissionIds = resourcePermissions.map(p => p.id);
      const allSelected = resourcePermissionIds.every(id => selectedPermissionIds.includes(id));
      
      if (allSelected) {
        // Deselect all in this resource
        onChange(selectedPermissionIds.filter(id => !resourcePermissionIds.includes(id)));
      } else {        // Select all in this resource
        const allPermissions = new Set([...selectedPermissionIds, ...resourcePermissionIds]);
        const newSelection = Array.from(allPermissions);
        onChange(newSelection);
      }
    };

    return (
      <Box>
        {Object.entries(groupedPermissions).map(([resource, resourcePermissions]) => {
          const resourcePermissionIds = resourcePermissions.map(p => p.id);
          const selectedCount = resourcePermissionIds.filter(id => selectedPermissionIds.includes(id)).length;
          const allSelected = selectedCount === resourcePermissionIds.length;
          const someSelected = selectedCount > 0;

          return (
            <Accordion key={resource}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={() => handleResourceToggle(resource)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                        {resource}
                      </Typography>
                      <Chip size="small" label={`${selectedCount}/${resourcePermissionIds.length}`} />
                    </Box>
                  }
                  onClick={(e) => e.stopPropagation()}
                />
              </AccordionSummary>
              <AccordionDetails>
                <FormGroup>
                  {resourcePermissions.map(permission => (
                    <FormControlLabel
                      key={permission.id}
                      control={
                        <Checkbox
                          checked={selectedPermissionIds.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">
                            {permission.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {permission.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <PermissionGate permission="roles:read" fallback={
      <Alert severity="error" sx={{ mt: 2 }}>
        You don't have permission to manage roles.
      </Alert>
    }>
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                Role Management
              </Typography>
              <PermissionGate permissions={["roles:create", "roles:manage"]}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setIsCreateDialogOpen(true)}
                >
                  Create Role
                </Button>
              </PermissionGate>
            </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Permissions</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id} hover>
                    <TableCell>
                      <Chip
                        label={role.name}
                        color={getRoleColor(role.name) as any}
                        icon={<SecurityIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {role.description || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${role.permissions.length} permissions`}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(role.createdAt).toLocaleDateString()}
                    </TableCell>                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <PermissionGate permissions={["roles:update", "roles:manage"]}>
                          <Tooltip title="Edit Role">
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(role)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </PermissionGate>
                        <PermissionGate permissions={["roles:delete", "roles:manage"]}>
                          <Tooltip title="Delete Role">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDeleteDialog(role)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </PermissionGate>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Role Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            Create New Role
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField
              label="Role Name"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              required
            />
            <TextField
              label="Description"
              multiline
              rows={2}
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
            />
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Permissions
            </Typography>
            <PermissionSelector
              selectedPermissionIds={createForm.permissionIds}
              onChange={(permissionIds) => setCreateForm({ ...createForm, permissionIds })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateRole} variant="contained">Create Role</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Role</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField
              label="Role Name"
              value={editForm.name || ''}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            />
            <TextField
              label="Description"
              multiline
              rows={2}
              value={editForm.description || ''}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            />
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Permissions
            </Typography>
            <PermissionSelector
              selectedPermissionIds={editForm.permissionIds || []}
              onChange={(permissionIds) => setEditForm({ ...editForm, permissionIds })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditRole} variant="contained">Update Role</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Role Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete role "{selectedRole?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteRole} color="error" variant="contained">
            Delete
          </Button>        </DialogActions>
      </Dialog>
    </Box>
    </PermissionGate>
  );
};

export default RoleManagementPage;
